import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import history from '../../history';
import {
  getDistinctCustomDocTitles,
  fetchCustomDocumentTemplates,
  fetchDocumentsByType
} from '../../api/v4';
import { addMessage } from '../../actions/messages';

import { Dropdown } from '../../components/inputs';
import HeaderAndFooter from '../../components/HeaderAndFooter';
import Header from '../../components/Header';
import List from '../../components/List';
import Modal from '../../components/Modal';
import { useSocket } from '../../utils/withSocket';
import useActiveHeirarchy from '../../utils/useActiveHeirarchy';
import { getLoggedInUser } from '../../selectors/users';
import { getActiveCompany } from '../../selectors/company';

export default function CustomDocumentList() {
  const dispatch = useDispatch();
  const socket = useSocket();
  const { company, location, project } = useActiveHeirarchy();
  const [templates, setTemplates] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [titles, setTitles] = useState([]);
  const [generated, setGenerated] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedTitle, setSelectedTitle] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [openCreateDocumentModal, setOpenCreateDocumentModal] = useState(false);
  const [openExportDocumentModal, setOpenExportDocumentModal] = useState(false);
  const [showDownloadModal, setDownloadModal] = useState(false);

  const activeUser = useSelector(getLoggedInUser);
  const activeCompany = useSelector(getActiveCompany);

  useEffect(() => {
    fetchCustomDocumentTemplates().then(response =>
      setTemplates(
        response?.map(template => ({
          value: template._id,
          label: template.label
        }))
      )
    );
    fetchDocumentsByType('CustomDocument').then(setDocuments);
    getDistinctCustomDocTitles().then(response => {
      setTitles(response.map(r => ({ label: r, value: r })));
    });
  }, []);

  useEffect(() => {
    if (!socket) return;
    socket.emit('subscribeCustomDocList', {
      company: company?._id,
      group: location?._id,
      project: project?._id
    });

    socket.on('customDocCSV', value => {
      setGenerated(value ?? {});
      if (value?.url?.length) setLoading(false);
    });
    socket.on('printFail', error => {
      setGenerated(null);
      dispatch(
        addMessage({
          error: true,
          message: `Error Creating Custom Doc CSVs: ${error}`
        })
      );
    });
  }, [company, location, project, socket, dispatch]);

  const handleExportDocument = () => {
    socket.emit('generateCustomDocCSV', {
      company: company?._id,
      group: location?._id,
      project: project?._id,
      customDocLabel: selectedTitle
    });
    setGenerated({});
    setLoading(true);
    setOpenExportDocumentModal(false);
    setDownloadModal(true);
    setSelectedTitle(null);
  };

  const header = (
    <Header
      title="Custom Documents"
      pageActionOptions={[
        {
          label: 'Create Document',
          visible: true,
          color: 'blueOutline',
          onClick: () => setOpenCreateDocumentModal(true)
        },
        {
          label: 'Export Custom Document',
          visible: !loading,
          color: 'blueOutline',
          onClick: () => setOpenExportDocumentModal(true)
        },
        {
          label: 'Creating Document CSV',
          visible: loading,
          color: 'greyOutline',
          disabled: true
        },
        {
          label: `Download ${generated.name} CSV`,
          visible: generated.url,
          color: 'blueOutline',
          onClick: () => window.open(generated.url, '_blank')
        }
      ]}
    />
  );

  const columns = [
    { key: 'label', label: 'Title' },
    {
      key: 'createdByUserId',
      label: 'Created By',
      datatype: 'user',
      accessor: r => r?.auditLog[0]?.user ?? r?.auditLog[0]?.userId
    },
    { key: 'createdAt', label: 'Created Date', datatype: 'date' },
    { key: 'updatedAt', label: 'Updated Date', datatype: 'date' }
  ];

  if (
    activeCompany?.usersWithCustomDocConfidentialAccess?.some(
      id => id === activeUser._id
    )
  ) {
    columns.splice(2, 0, {
      key: 'isConfidential',
      accessor: r => (r?.isConfidential ? 'Yes' : 'No'),
      enum: ['Yes', 'No']
    });
  }
  return (
    <>
      <HeaderAndFooter Header={header}>
        <List
          saveKey="customDocList"
          data={documents}
          dataIsHash
          settings={columns}
          rowClick={row =>
            history.push(`/app/customDocumentContainer/null/${row._id}`)
          }
        />
      </HeaderAndFooter>
      <Modal
        medium
        title="Create Custom Document"
        titleClassName="blueHeader"
        isOpen={openCreateDocumentModal}
        submitButtonColor="blue"
        submitButtonText="Create"
        onRequestClose={() => setOpenCreateDocumentModal(false)}
        submitActions={() =>
          history.push(`/app/customDocumentContainer/${selectedTemplateId}`)
        }
        disableSubmit={!selectedTemplateId}
        testID="Create Custom Document"
      >
        <Dropdown
          fieldLabel="Select Custom Document to create"
          options={templates}
          searchable
          name="templateDD"
          onChange={values => setSelectedTemplateId(values)}
          isRequired
          currentValue={selectedTemplateId}
        />
      </Modal>
      <Modal
        title="Export Custom Document"
        titleClassName="blueHeader"
        isOpen={openExportDocumentModal}
        submitButtonColor="blue"
        submitButtonText="Export"
        onRequestClose={() => setOpenExportDocumentModal(false)}
        submitActions={handleExportDocument}
        disableSubmit={!selectedTitle}
      >
        <Dropdown
          fieldLabel="Select Custom Document to export"
          options={titles}
          searchable
          name="titleDD"
          onChange={values => setSelectedTitle(values)}
          isRequired
          currentValue={selectedTitle}
        />
      </Modal>
      <Modal
        title="Export Custom Document"
        titleClassName="greenHeader"
        isOpen={showDownloadModal}
        onRequestClose={() => setDownloadModal(false)}
        hideButtons
      >
        Download Scheduled in the background, please check back later.
      </Modal>
    </>
  );
}
